import type { Components, Theme, ThemeOptions } from "@mui/material/styles";

import {
  borderWidth,
  color,
  effect,
  fontWeight,
  opacity,
  radius,
  spacing,
  transition,
} from "@app/theme/weaveTokens.ts";
import { typographyVariant } from "@app/theme/typography.ts";

type ThemeComponents = Components<Omit<Theme, "components">>;

/**
 * Restores the Digital HIG form field look. Weave's legacy theme has no
 * OutlinedInput, TextField, FormHelperText or FormLabel entries, so DHIG
 * Select, Search and TextField controls lose their field shadow, underline
 * states, helper text styling and (for the search input) their layout.
 */
const WEAVE_TEXT_FIELD_KEY = "& .MuiOutlinedInput-root:not(.MuiSelect-root)";

/**
 * Weave styles outlined text fields inside a FormControl with
 * `& .MuiOutlinedInput-root:not(.MuiSelect-root)` (8px 12px padding, zeroed
 * input padding, a boxed notched outline), meaning "not a Select". But a
 * Select's root never carries a `MuiSelect-root` class (it is only part of the
 * emotion label, which production builds drop), so the rule also hit every
 * DHIG Select inside a FormControl: the sidebar hub dropdown lost its value
 * padding and showed Weave's focus ring inside the box, a second outline.
 * This re-emits Weave's rule for roots that do not contain a Select value, so
 * it still reaches text fields and the combobox exactly as before. It reads
 * the rule from the merged Weave theme, so it keeps working after a Weave
 * upgrade (the rule is simply not re-emitted if Weave removes it).
 */
export const weaveSelectLeakFix = (
  weaveComponents: ThemeOptions["components"]
): ThemeOptions["components"] => {
  const root = weaveComponents?.MuiFormControl?.styleOverrides?.root;
  const rule =
    root && typeof root === "object"
      ? (root as Record<string, unknown>)[WEAVE_TEXT_FIELD_KEY]
      : undefined;
  if (!rule) return {};
  return {
    MuiFormControl: {
      styleOverrides: {
        root: {
          [WEAVE_TEXT_FIELD_KEY]: undefined,
          "& .MuiOutlinedInput-root:not(:has(> .MuiSelect-select))": rule,
        },
      },
    },
  } as ThemeOptions["components"];
};

export const fieldOverrides: ThemeComponents = {
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        boxShadow: effect.fieldDefault,
        borderRadius: `${radius.medium} ${radius.medium} 0 0`,
        outline: "none",
        '&:not(.Mui-disabled) [class*="MuiOutlinedInput-input"]:before': {
          left: 0,
          right: 0,
          bottom: 0,
          display: "block",
          height: "100%",
          content: "''",
          position: "absolute",
        },
        '& [class*="MuiSelect-icon"], [class*="MuiNativeSelect-icon"]': {
          color: color.ink,
          marginRight: spacing.xxs,
        },
        "&.Mui-error": {
          boxShadow: effect.fieldError,
          '&:hover, & [class*="MuiOutlinedInput-input"]:focus-visible:before': {
            boxShadow: effect.fieldError,
          },
          "&.Mui-focused": { boxShadow: effect.fieldError },
        },
        "&.Mui-disabled": {
          opacity: opacity.disabled,
          "& .MuiSvgIcon-root": { color: "inherit" },
        },
      },
      input: {
        padding: `calc(${spacing.px20}/2) ${spacing.s}`,
        height: "auto",
        "&.Mui-error": {
          "&:focus:before": { boxShadow: "none" },
          "&:hover:before": {
            boxShadow: `0px 2px 0px 0px ${color.transparent}`,
          },
        },
        "&[aria-expanded='true']": {
          borderBottomWidth: borderWidth.none,
          borderRadius: `${radius.medium} ${radius.medium} 0 0`,
          boxShadow: effect.focusRingThin,
          "&:before": {
            boxShadow: `0px 2px 0px 0px ${color.iris}`,
          },
          "&.Mui-error": {
            boxShadow: "none",
            "&:before": {
              boxShadow: `0px 2px 0px 0px ${color.transparent}`,
            },
          },
        },
        "&:hover:before": {
          borderBottomWidth: borderWidth.none,
          boxShadow: `0px 1px 0px 0px ${color.irisHover}`,
          transitionTimingFunction: transition.blur.transitionTimingFunction,
          transitionDuration: transition.blur.transitionDuration,
        },
        "&:focus:before, &:focus-visible:before": {
          borderBottomWidth: borderWidth.none,
          borderRadius: `${radius.medium} ${radius.medium} 0 0`,
          boxShadow: effect.focusRingThin,
        },
      },
      notchedOutline: {
        border: 0,
        borderRadius: `${radius.medium} ${radius.medium} 0 0`,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& [class*='MuiInputBase-root'][class*='MuiOutlinedInput-root']": {
          border: "1px solid transparent",
          borderRadius: 0,
          paddingRight: spacing.none,
          ...transition.activate,
          "&.Mui-focused, &:focus-within": {
            border: `1px solid ${color.irisHover}`,
            boxShadow: `0px -1px 0px 0px ${color.ink} inset, 0px 0px 0px 1px ${color.irisHover} inset`,
          },
          "&:hover": {
            borderBottom: `1px solid ${color.irisHover}`,
          },
          "&.Mui-error": {
            boxShadow: effect.fieldError,
            border: `1px solid ${color.transparent}`,
            "&:hover": {
              borderBottomColor: color.transparent,
              "&.Mui-focused": {
                boxShadow: effect.fieldError,
                border: `1px solid ${color.transparent}`,
              },
            },
          },
          "&.Mui-disabled": {
            borderBottomColor: color.transparent,
          },
          "& [class*='MuiOutlinedInput-input']": {
            ...typographyVariant["input-value"],
            "&::placeholder": { ...typographyVariant["short-copy-medium"] },
            "&.Mui-disabled": {
              WebkitTextFillColor: color.ink,
            },
            "&::selection": {
              background: color.pressedFillSubtle,
            },
          },
          '& [class*="MuiInputAdornment-positionEnd"]': {
            marginLeft: spacing.xxs,
            marginRight: spacing.xxs,
          },
        },
      },
    },
  },
  // DHIG's `Input` (standard variant) rules that Weave's MuiInput slot lacks:
  // no root margin or padding, and the 24px value box with DHIG's input-value
  // typography (Weave's input is 23px high, so every `Input` was 1px short and
  // sat 16px below its label). These go in the `root` slot on purpose: Weave's
  // `underline` slot has a key with an unbalanced quote
  // (`&[class*=designTokensMuiInputBase-adornedEnd"]`) that makes the CSS
  // parser drop every rule written after it, including any added there.
  MuiInput: {
    styleOverrides: {
      root: {
        '&[class*="MuiInput-underline"]': {
          marginTop: spacing.none,
          padding: spacing.none,
          "&.Mui-focused:not(.Mui-error)": {
            boxShadow: `0px 1px 0px 0px ${color.inkLinkLoud} inset, 0px 0px 0px 1px ${color.inkLinkLoud} inset`,
          },
          '& [class*="MuiInputBase-input"]': {
            ...typographyVariant["input-value"],
            height: spacing.l,
            color: color.ink,
          },
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        marginTop: spacing.xxs,
        color: color.inkMuted,
        ...typographyVariant["short-copy-small"],
        display: "flex",
        alignItems: "flex-start",
        "& > svg": { paddingRight: spacing.xxs },
        "&.Mui-error": { fontWeight: fontWeight.medium, opacity: 1 },
      },
      contained: { marginLeft: 0, marginRight: 0 },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        // Weave pins label typography to 12px here with a descendant selector,
        // which beats the `css`/`style` font sizes call sites set on labels
        // (for example `fontSize: "1em"`). `undefined` unsets those keys in
        // the merged theme, so the label falls back to the InputLabel rule
        // below and call-site sizes apply again, as under DHIG.
        // DHIG keeps the Input flush under its label (MUI's own 16px
        // `label + Input` gap is for floating labels).
        '& label + [class*="MuiInput-formControl"]': { marginTop: 0 },
        // Weave indents Typography inside a label by 6px; DHIG pads it by 8px
        // (see the InputLabel rule below), which call sites zero out with
        // `pl: 0 !important`. Unset Weave's so the zero is not 6px.
        "& .MuiFormLabel-root.MuiInputLabel-root": {
          "& .MuiTypography-root, & .MuiSvgIcon-root": {
            marginLeft: undefined,
          },
        },
        "& .MuiFormLabel-root": {
          fontFamily: undefined,
          fontSize: undefined,
          lineHeight: undefined,
          fontWeight: undefined,
          letterSpacing: undefined,
          // Weave also pins a 4px bottom margin here, which beats a call
          // site's `sx={{ marginBottom: 0 }}` (the Owner filter label on the
          // app index sat 2px high). The InputLabel rule restores the 4px.
          marginBottom: undefined,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        marginBottom: spacing.xxs,
        color: color.ink,
        ...typographyVariant["smallprint"],
        maxWidth: "100%",
        "& .DhigInputLabel--hintText, &:not(.Mui-required) > [class*='MuiTypography']":
          {
            paddingLeft: spacing.xs,
            color: color.inkMuted,
          },
        "&.Mui-error": { color: color.ink },
        "&.Mui-disabled": { opacity: opacity.disabled },
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        "&.Mui-error": { color: color.ink },
        "&.Mui-disabled": { color: color.ink },
      },
      asterisk: { display: "none" },
    },
  },
};
